import apiClient from '../apiClient';

const mapProduct = (product) => {
  if (!product) return null;
  const onSale = product.on_sale;
  const currentPrice = product.price;
  const originalPrice = onSale && product.sale_price ? product.sale_price : null;

  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    description: product.description,
    price: currentPrice,
    originalPrice,
    onSale,
    isNew: product.is_new,
    isBestSeller: product.is_best_seller,
    isFeatured: product.is_featured,
    stock: product.stock,
    category: product.category_id,
    subcategory: product.subcategory_id,
    sizeGuideId: product.size_guide_id || null,
    sizes: product.sizes || [],
    colors: product.colors || [],
    colorNames: product.color_names || [],
    material: product.material || '',
    care: product.care || '',
    images: (product.images || [])
      .slice()
      .sort((a, b) => {
        if (typeof a === 'string' || typeof b === 'string') return 0;
        return (a.order ?? 0) - (b.order ?? 0);
      })
      .map((image) => (typeof image === 'string' ? image : image.url || image)),
    createdAt: product.created_at,
    updatedAt: product.updated_at,
  };
};

const mapProductPayload = (productData) => ({
  name: productData.name,
  slug: productData.slug,
  description: productData.description,
  price: productData.price,
  sale_price: productData.onSale ? productData.originalPrice : null,
  on_sale: !!productData.onSale,
  is_new: !!productData.isNew,
  is_best_seller: !!productData.isBestSeller,
  is_featured: !!productData.isFeatured,
  stock: productData.stock,
  category_id: productData.category || null,
  subcategory_id: productData.subcategory || null,
  size_guide_id: productData.sizeGuideId || null,
  sizes: productData.sizes || [],
  colors: productData.colors || [],
  color_names: productData.colorNames || [],
  material: productData.material || null,
  care: productData.care || null,
  images: productData.images || [],
});

/**
 * Get all products with pagination
 * @param {number} pageSize - Number of products per page
 * @param {number} lastDoc - Last document for pagination (optional)
 * @param {string} sortBy - Sort field (default: 'createdAt')
 * @param {string} sortOrder - Sort order: 'asc' or 'desc' (default: 'desc')
 * @returns {Promise<{products: Array, lastDoc: DocumentSnapshot}>}
 */
export const getProductsPaginated = async (
  pageSize = 20,
  page = 1,
  sortBy = 'createdAt',
  sortOrder = 'desc'
) => {
  try {
    const sort = sortBy === 'price' && sortOrder === 'asc'
      ? 'price_asc'
      : sortBy === 'price' && sortOrder === 'desc'
        ? 'price_desc'
        : sortBy === 'name' && sortOrder === 'asc'
          ? 'name_asc'
          : sortBy === 'name' && sortOrder === 'desc'
            ? 'name_desc'
            : 'newest';
    const response = await apiClient.get('/admin/products', {
      params: { page, page_size: pageSize, sort },
    });
    const products = response.data.map(mapProduct);
    const hasMore = products.length === pageSize;
    return { products, nextPage: page + 1, hasMore };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Search products by name
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Array of matching products
 */
export const searchProducts = async (searchTerm) => {
  try {
    const response = await apiClient.get('/admin/products/search', {
      params: { q: searchTerm },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error searching products:', error);
    throw error;
  }
};

/**
 * Get product by ID
 * @param {string} productId - Product ID
 * @returns {Promise<Object>} Product object
 */
export const getProductById = async (productId) => {
  try {
    const response = await apiClient.get(`/admin/products/${productId}`);
    return mapProduct(response.data);
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

/**
 * Create a new product
 * @param {Object} productData - Product data
 * @returns {Promise<Object>} Created product with ID
 */
export const createProduct = async (productData) => {
  try {
    const response = await apiClient.post('/admin/products', mapProductPayload(productData));
    return mapProduct(response.data);
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

/**
 * Update a product
 * @param {string} productId - Product ID
 * @param {Object} productData - Updated product data
 * @returns {Promise<void>}
 */
export const updateProduct = async (productId, productData) => {
  try {
    await apiClient.put(`/admin/products/${productId}`, mapProductPayload(productData));
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

/**
 * Delete a product
 * @param {string} productId - Product ID
 * @param {Array} imageUrls - Array of image URLs to delete from storage
 * @returns {Promise<void>}
 */
export const deleteProduct = async (productId, imageUrls = []) => {
  try {
    await apiClient.delete(`/admin/products/${productId}`);
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

/**
 * Upload multiple product images
 * @param {Array<File>} files - Array of image files
 * @param {string} productId - Product ID (use 'temp' for new products)
 * @returns {Promise<Array<string>>} Array of download URLs
 */
export const uploadProductImages = async (files, productId = 'temp') => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const response = await apiClient.post(`/admin/products/${productId}/images`, formData);
    return response.data?.urls || [];
  } catch (error) {
    console.error('Error uploading product images:', error);
    throw error;
  }
};

/**
 * Bulk create products from array
 * @param {Array<Object>} productsData - Array of product data objects
 * @returns {Promise<void>}
 */
export const bulkCreateProducts = async (productsData) => {
  try {
    const payload = productsData.map(mapProductPayload);
    const response = await apiClient.post('/admin/products/bulk', payload);
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error bulk creating products:', error);
    throw error;
  }
};

/**
 * Bulk create products from CSV file
 * @param {File} file - CSV file
 * @returns {Promise<Array>} Array of created products
 */
export const bulkCreateProductsFromCsv = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/admin/products/bulk-csv', formData);
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error bulk creating products from CSV:', error);
    throw error;
  }
};

/**
 * Bulk delete products
 * @param {Array<number>} productIds - Product IDs
 * @returns {Promise<Array<number>>} Deleted IDs
 */
export const bulkDeleteProducts = async (productIds) => {
  try {
    const response = await apiClient.post('/admin/products/bulk-delete', { ids: productIds });
    return response.data?.deleted_ids || productIds;
  } catch (error) {
    console.error('Error bulk deleting products:', error);
    throw error;
  }
};
